/**
 * CLI activation — the browser half of the device-authorization handshake
 * (blueprint §6.2).
 *
 * This page has two things to do well: prove the request is theirs — by showing what
 * the terminal reported — and tell them clearly to go back to the terminal. It used to
 * have a third, choosing a plan: the account is already
 * on Archis Free, so there is nothing to pick. The activation code is one-time-use and
 * expires in ten minutes, so there is no room for ambiguity.
 */

import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useSession } from '@/app/providers'
import { useApproveActivation, usePendingActivation } from '@/shared/api/hooks'
import {
  Badge,
  Button,
  ErrorNotice,
  Fig,
  Label,
  Loading,
  Notice,
  formatRelative,
  platformLabel,
} from '@/shared/ui/primitives'
import { Masthead } from '@/shared/ui/Chrome'

export function Activate() {
  const [params, setParams] = useSearchParams()
  const { user, ready } = useSession()
  const [typedCode, setTypedCode] = useState('')

  const userCode = (params.get('user_code') ?? '').toUpperCase()
  const { data: pending, isPending, error } = usePendingActivation(user ? userCode : '')
  const approve = useApproveActivation()


  // Re-affirm the code in the URL once it is typed, so a refresh keeps working and
  // the link is shareable back to the same state.
  useEffect(() => {
    if (!userCode && typedCode.length >= 8) {
      setParams({ user_code: typedCode.toUpperCase() }, { replace: true })
    }
  }, [typedCode, userCode, setParams])

  if (!ready) {
    return (
      <>
        <Masthead />
        <Loading label="Checking your session" />
      </>
    )
  }

  // Sign-in first, carrying the code so the person lands back here afterwards.
  if (!user) {
    return (
      <>
        <Masthead />
        <main id="main" className="shell" style={{ paddingBlock: 'var(--s8)', maxWidth: 480 }}>
          <div className="panel panel--flush">
            <div className="panel__head">
              <Label index="00">Activate Archis</Label>
            </div>
            <div className="panel__body stack">
              <p className="dim">
                Sign in to approve the login your terminal requested
                {userCode && (
                  <>
                    {' '}
                    for code{' '}
                    <span className="ph-no-capture">
                      <Fig>{userCode}</Fig>
                    </span>
                  </>
                )}
                .
              </p>
              <Link to={`/signin?user_code=${encodeURIComponent(userCode)}`}>
                <Button variant="primary" block size="lg">
                  Sign in to continue
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </>
    )
  }

  return (
    <>
      <Masthead />
      <main id="main" className="shell" style={{ paddingBlock: 'var(--s8)', maxWidth: 560 }}>
        <div className="panel panel--flush">
          <div className="panel__head">
            <Label index="00">Activate Archis</Label>
            {pending && <Badge tone="amber">expires {formatRelative(pending.expires_at)}</Badge>}
          </div>

          <div className="panel__body stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            {/* No code in the URL: the person opened the page manually. */}
            {!userCode && (
              <div className="stack">
                <h3>Enter your verification code</h3>
                <p className="mono-note">Your terminal printed a code like ABCD-EFGH.</p>
                <input
                  className="input input--mono ph-no-capture"
                  value={typedCode}
                  onChange={(event) => setTypedCode(event.target.value.toUpperCase())}
                  placeholder="ABCD-EFGH"
                  maxLength={9}
                  autoFocus
                  aria-label="Verification code"
                />
              </div>
            )}

            {userCode && isPending && <Loading label="Looking up your activation request" />}

            <ErrorNotice error={error} />

            {pending && !approve.data && (
              <>
                <div className="stack" style={{ ['--gap' as string]: 'var(--s2)' }}>
                  <h3>Approve this device?</h3>
                  <p className="mono-note">
                    A terminal reported the details below. If you did not start this, close
                    this page and nothing happens.
                  </p>
                </div>

                {/* Showing what the terminal reported is what makes approval meaningful
                    rather than a blind click. */}
                <div className="term">
                  <div className="term__bar">requested activation</div>
                  <div className="term__body">
                    <div className="term__line">
                      <span className="term__dim">code{'      '}</span>
                      <span className="term__ok ph-no-capture">{pending.user_code}</span>
                    </div>
                    <div className="term__line">
                      <span className="term__dim">device{'    '}</span>
                      {pending.display_name || 'Archis CLI'}
                    </div>
                    <div className="term__line">
                      <span className="term__dim">platform{'  '}</span>
                      {platformLabel(pending.platform, pending.arch)}
                    </div>
                    <div className="term__line">
                      <span className="term__dim">version{'   '}</span>
                      chi {pending.cli_version}
                    </div>
                  </div>
                </div>

                <ErrorNotice error={approve.error} />

                <Button
                  variant="primary"
                  size="lg"
                  block
                  loading={approve.isPending}
                  onClick={() => approve.mutate(pending.session_id)}
                >
                  Approve and continue
                </Button>
              </>
            )}

            {approve.data?.approved && (
              <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
                <Notice tone="ok">
                  Approved. Return to your terminal — the CLI has your credentials.
                </Notice>


                <div className="term">
                  <div className="term__bar">your terminal</div>
                  <div className="term__body">
                    <div className="term__line term__ok">✓ Signed in</div>
                    <div className="term__line term__dim">
                      {'  '}Run <span className="term__prompt">chi index .</span> to get started
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  )
}
